import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { MapPin, Package, Calendar } from 'lucide-react';
import { formatQuoteRequestDate, isUnspecifiedDateType } from '../../lib/format';
import type { CargoQuoteRequest } from '../../types/quotes';

interface QuoteInfoSectionProps {
  quote: CargoQuoteRequest;
}

export const QuoteInfoSection: React.FC<QuoteInfoSectionProps> = ({ quote }) => {
  const { t, i18n } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language;

  const translateMethod = (method: string): string => {
    return t(`quote.methods.${method}`, { defaultValue: method }) as string;
  };

  // Route dates keep their precision in the active language (#287): exact day
  // for a fixed request, month + year for a flexible (month-only) one, no
  // fabricated day; an unspecified request shows a localized "no date" note.
  const originDate = formatQuoteRequestDate(
    quote.date_type,
    quote.date_type === 'fixed' ? quote.origin_fixed_date : quote.origin_date,
    { language },
  );
  const destinationDate = formatQuoteRequestDate(
    quote.date_type,
    quote.date_type === 'fixed' ? quote.destination_fixed_date : quote.destination_date,
    { language },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-blue-600" />
          {t('admin.quote.route_information')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <span className="text-xs text-slate-500 uppercase">{t('admin.quote.origin')}</span>
            <p className="text-lg">{quote.origin_city}, {quote.origin_country}</p>
            <p className="text-sm text-slate-600">
              {t('admin.quote.zip_value', { zip: quote.origin_zip_code })}
            </p>
            {quote.origin_street && (
              <p className="text-sm text-slate-600">{quote.origin_street}</p>
            )}
            {originDate ? (
              <div className="mt-2 flex items-center text-sm text-blue-700">
                <Calendar className="h-3 w-3 mr-1" />
                {t('admin.quote.date_value', { date: originDate })}
              </div>
            ) : isUnspecifiedDateType(quote.date_type) ? (
              <div className="mt-2 flex items-center text-sm text-slate-500">
                <Calendar className="h-3 w-3 mr-1" />
                {t('admin.quote.no_specific_date')}
              </div>
            ) : null}
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <span className="text-xs text-slate-500 uppercase">{t('admin.quote.destination')}</span>
            <p className="text-lg">{quote.destination_city}, {quote.destination_country}</p>
            <p className="text-sm text-slate-600">
              {t('admin.quote.zip_value', { zip: quote.destination_zip_code })}
            </p>
            {quote.destination_street && (
              <p className="text-sm text-slate-600">{quote.destination_street}</p>
            )}
            {destinationDate ? (
              <div className="mt-2 flex items-center text-sm text-blue-700">
                <Calendar className="h-3 w-3 mr-1" />
                {t('admin.quote.date_value', { date: destinationDate })}
              </div>
            ) : isUnspecifiedDateType(quote.date_type) ? (
              <div className="mt-2 flex items-center text-sm text-slate-500">
                <Calendar className="h-3 w-3 mr-1" />
                {t('admin.quote.no_specific_date')}
              </div>
            ) : null}
          </div>
        </div>

        <div>
          <h4 className="text-sm mb-2 flex items-center gap-2">
            <Package className="h-4 w-4" /> {t('admin.quote.cargo_details')}
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div className="min-w-0 border p-3 rounded">
                <span className="block text-slate-500 text-xs">{t('admin.quote.volume')}</span>
                <span className="font-medium break-words">
                  {t('admin.quote.volume_value', { value: quote.cargo_volume_m3 })}
                </span>
            </div>
            {quote.cargo_weight && (
              <div className="min-w-0 border p-3 rounded">
                <span className="block text-slate-500 text-xs">{t('admin.quote.weight')}</span>
                <span className="font-medium break-words">
                  {t('admin.quote.weight_value', { value: quote.cargo_weight })}
                </span>
              </div>
            )}
            <div className="min-w-0 border p-3 rounded">
              <span className="block text-slate-500 text-xs">{t('admin.quote.dimensions')}</span>
              <span className="font-medium break-words">
                {t('admin.quote.dimensions_value', {
                  length: quote.cargo_length,
                  width: quote.cargo_width,
                  height: quote.cargo_height
                })}
              </span>
            </div>
            <div className="min-w-0 border p-3 rounded">
              <span className="block text-slate-500 text-xs">{t('admin.quote.methods')}</span>
              <span className="font-medium break-words">
                {t('admin.quote.methods_list', {
                  methods: quote.transportation_methods.map(translateMethod).join(', ')
                })}
              </span>
            </div>
          </div>
        </div>

        {quote.observations && (
          <div className="bg-amber-50 p-4 rounded-md border border-amber-100">
            <h4 className="text-xs text-amber-800 uppercase mb-1">{t('admin.quote.observations_label')}</h4>
            <p className="text-sm text-amber-900">{quote.observations}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
